import { Inject, Injectable } from '@angular/core';
import { UpdatePointsAccountId } from '@features/user-preference/store/actions/user-preference.actions';
import { getUserPreferences } from '@features/user-preference/store/reducers';
import { UserPreferenceState } from '@features/user-preference/store/reducers/user-preference.reducer';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AuthActionTypes } from '@rd/core/auth/store/actions/auth.actions';
import { INCLUDE_CLOSING_POINTS_ACCOUNTS } from '@rd/core/tokens/rd-lib.token';
import { HttpError, PointsAccount, UserPreference } from '@rd/types';
import { EMPTY, of } from 'rxjs';
import {
  catchError,
  map,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs/operators';
import { PointsAccountApiService } from '../../services/points-acccount-api/points-account-api.service';
import * as actions from '../actions/points-account.actions';

@Injectable()
export class PointsAccountEffects {
  constructor(
    private actions$: Actions,
    private pointsAccountApiService: PointsAccountApiService,
    private store: Store<UserPreferenceState>,
    @Inject(INCLUDE_CLOSING_POINTS_ACCOUNTS)
    private includeClosingPointsAccounts: boolean,
  ) {}

  loadPointsAccounts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.PointsAccountActionTypes.LOAD_POINTS_ACCOUNTS),
      switchMap((action: actions.LoadPointsAccounts) => {
        return this.pointsAccountApiService
          .getPointsAccounts(action.payload)
          .pipe(
            map((pointsAccounts: PointsAccount[]) => {
              return new actions.LoadPointsAccountsSuccess(pointsAccounts);
            }),
            catchError((error: HttpError) =>
              of(new actions.LoadPointsAccountsFail(error)),
            ),
          );
      }),
    ),
  );

  // This effect will only run for White labels that have points transactions enabled
  // which means that there will always be a points_account_id set in GuardHouse
  // from the enrollment process
  loadPointsAccountsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.PointsAccountActionTypes.LOAD_POINTS_ACCOUNTS_SUCCESS),
      withLatestFrom(this.store.select(getUserPreferences)),
      switchMap(
        ([action, { pointsAccountId }]: [
          actions.LoadPointsAccountsSuccess,
          UserPreference,
        ]) => {
          const pointsAccountIds = action.payload.map((p) => p.id);

          if (
            // if points accounts id doesn't have current points_account_id set from user preference
            // That means that user preference is pointed to a closed account
            pointsAccountIds.length > 0 &&
            // @ts-ignore
            !pointsAccountIds.includes(pointsAccountId)
          ) {
            return [new UpdatePointsAccountId(pointsAccountIds[0])];
          }

          return EMPTY;
        },
      ),
    ),
  );

  pullPointsAccountsOnUserLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActionTypes.LOGIN_USER),
      take(1),
      map(() => {
        return new actions.LoadPointsAccounts({
          includeClosingPointsAccounts: this.includeClosingPointsAccounts,
        });
      }),
    ),
  );
}
